// Deutsches Wörterbuch der Self-Service-Fläche — die QUELLE aller Schlüssel
// (Spec 2026-09-15 Sprachumschalter). `en.ts` ist als `Messages` getippt, ein
// fehlender Schlüssel dort ist ein Compilerfehler. Texte mit Platzhaltern sind
// Funktionen. Feldnamen stehen nicht hier, sondern in `selfFieldLabels.ts`
// (dort hängen Konfliktdialog und Merge-Toasts schon dran).
//
// Nicht hier: Vokabular-Werte (Kategorie/Dialekt/Akzent), Dateinamen und die
// Lösch-Mail an TRO — das sind Daten bzw. gehen an das deutsche TRO-Team.
import { failureReason } from '@/lib/failureReason';
import { saveStatusText } from '@/lib/saveStatusText';

export const de = {
  // Umschalter
  langToggleAria: 'Sprache wechseln',

  // SelfApp: Lade- und Fehlerzustände
  invalidLink: 'Ungültiger Link.',
  loading: 'Lädt …',

  // Anmeldeseite
  profileTitle: 'Sprecher-Profil',
  loginHint: 'Bitte gib das Passwort ein, das du von uns erhalten hast, um deine Stammdaten und Sprachproben zu pflegen.',
  passwordLabel: 'Passwort',
  loginButton: 'Anmelden',
  loginBusy: 'Anmelden …',
  loginErrorInvalid: 'Falsches Passwort.',
  loginErrorTooMany: 'Zu viele Versuche, bitte später erneut.',
  loginErrorGeneric: 'Anmeldung fehlgeschlagen. Bitte später erneut versuchen.',

  // Profilseite: Abschnitte und Hinweise
  sectionBasics: 'Stammdaten',
  ownStudioCheckbox: 'Eigenes Studio vorhanden',
  sectionContact: 'Kontakt',
  sectionLanguages: 'Sprachen & Klang',
  languagesPlaceholder: 'Sprachen wählen',
  dialectsPlaceholder: 'Dialekte wählen',
  accentsPlaceholder: 'Akzente wählen',
  lockedBySample: 'durch Sprachprobe belegt',

  // Upload-Staging
  sectionUpload: 'Neue Sprachproben hochladen',
  dropzone: 'Audio-Dateien hierher ziehen oder klicken (wird automatisch zu MP3 konvertiert)',
  removeStaged: 'Entfernen',
  metaCategory: 'Kategorie',
  metaLanguage: 'Sprache',
  metaDialect: 'Dialekt',
  metaAccent: 'Akzent',
  metaFreetext: 'Freitext',
  freetextPlaceholder: 'optional',
  demomix: 'DemoMix (Zusammenschnitt)',
  noCategory: 'Ohne Kategorie',
  noDialect: 'Kein Dialekt',
  noAccent: 'Kein Akzent',
  legacyValue: '(Altwert)',

  // Probenliste
  sectionSamples: 'Deine Sprachproben',
  renameFailuresHeading: (n: number) => (n === 1
    ? 'Eine Sprachprobe konnte nicht umbenannt werden:'
    : `${n} Sprachproben konnten nicht umbenannt werden:`),
  failureReason: (error: string) => failureReason(error),
  retry: 'Erneut versuchen',
  noSamples: 'Noch keine Sprachproben hinterlegt.',
  renameHint: 'wird beim Speichern umbenannt',
  mailHint: 'Wenn eine Sprachprobe entfernt werden soll, schick uns bitte eine Mail und wir löschen sie aus dem System.',
  requestDeletion: 'Löschung anfragen',

  // Speichern-Leiste und Overlay
  saveButton: 'Speichern',
  savingButton: 'Speichern …',
  saving: 'Speichert …',
  saveStatus: (renameCount: number) => saveStatusText(renameCount, 'de'),
  convertProgress: (done: number, total: number) => `Konvertiere ${done}/${total} …`,
  uploadProgress: (up: number, total: number) => `Lädt hoch (${up}/${total}) …`,

  // Toasts
  skippedNotAudio: (n: number) => `${n}× kein Audio-Format`,
  skippedTooLarge: (n: number) => `${n}× zu groß (max. 300 MB)`,
  skipped: (parts: string) => `Übersprungen: ${parts}.`,
  saved: 'Deine Änderungen wurden gespeichert.',
  savedWithRenameFailures: (n: number) => (n === 1
    ? 'Gespeichert, aber eine Sprachprobe konnte nicht umbenannt werden — siehe Liste.'
    : `Gespeichert, aber ${n} Sprachproben konnten nicht umbenannt werden — siehe Liste.`),
  saveFailed: 'Speichern fehlgeschlagen. Bitte versuch es später erneut.',
  merged: (labels: string) => `Zusammengeführt: ${labels} von TRO übernommen.`,
  adoptedFields: (labels: string) => `Neue Daten von TRO übernommen: ${labels}.`,
  adopted: 'Neue Daten von TRO übernommen.',
  adoptLoadFailed: 'Neue Daten von TRO konnten nicht geladen werden. Neuer Versuch folgt.',
  keptMine: 'Eigene Werte behalten, nichts gespeichert.',

  // Konflikt-Dialog
  conflictTitle: 'Gleichzeitig bearbeitet',
  conflictIntro: 'Zwischenzeitlich von TRO bearbeitet.',
  conflictFields: (labels: string) => `Diese Felder wurden von beiden Seiten unterschiedlich geändert: ${labels}.`,
  conflictReloadFailed: 'Der aktuelle Stand konnte nicht geladen werden.',
  conflictRepeated: 'Der Datensatz wurde mehrfach gleichzeitig geändert.',
  conflictKeepMine: 'Meine behalten',
  conflictTakeTheirs: 'Fremde übernehmen',
  conflictOk: 'OK',
};

/** Form jedes Wörterbuchs — abgeleitet aus `de` (Texte als `string`, Funktionen mit ihren Parametern). */
export type Messages = typeof de;
